import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Code, Music } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-css";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism-tomorrow.css";
import { useAuth, API } from "@/App";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DOMPurify from "dompurify";

const DEFAULT_CSS = `/* Customize your profile styles! */
/* Example: Change background color */
/*
.custom-profile-container {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
  padding: 20px;
  border-radius: 0;
}
*/`;

const DEFAULT_HTML = `<!-- Add custom HTML to your profile! -->
<!-- Example: -->
<!--
<div style="text-align: center; padding: 20px;">
  <h2 style="font-family: 'Comic Sans MS', cursive;">Welcome to my page!</h2>
  <p>Thanks for visiting! ✨</p>
  <marquee>This is my cool profile!</marquee>
</div>
-->`;

export default function CustomizeProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("html");
  
  const [customCSS, setCustomCSS] = useState(DEFAULT_CSS);
  const [customHTML, setCustomHTML] = useState(DEFAULT_HTML);
  const [profileSong, setProfileSong] = useState({
    title: "",
    artist: "",
    url: "",
  });

  useEffect(() => {
    if (user) {
      setCustomCSS(user.custom_css || DEFAULT_CSS);
      setCustomHTML(user.custom_html || DEFAULT_HTML);
      if (user.profile_song) {
        setProfileSong(user.profile_song);
      }
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);

    try {
      const updateData = {
        custom_css: customCSS,
        custom_html: customHTML,
        profile_song: profileSong.title ? profileSong : null,
      };

      const response = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        await refreshUser();
        toast.success("Profile customization saved!");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save customization");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5E1]">
      {/* Header */}
      <header className="app-header">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="brutal-btn bg-white p-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold">Customize Profile</h1>
            <button
              onClick={handleSave}
              disabled={loading}
              className="brutal-btn-primary py-2 disabled:opacity-50"
              data-testid="save-btn"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor Section */}
          <div className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="brutal-card p-1 w-full grid grid-cols-3">
                <TabsTrigger
                  value="html"
                  className="data-[state=active]:bg-[#FF4D4D] data-[state=active]:text-white font-bold"
                  data-testid="tab-html"
                >
                  <Code className="w-4 h-4 mr-2" />
                  HTML
                </TabsTrigger>
                <TabsTrigger
                  value="css"
                  className="data-[state=active]:bg-[#FF66B2] data-[state=active]:text-white font-bold"
                  data-testid="tab-css"
                >
                  <Code className="w-4 h-4 mr-2" />
                  CSS
                </TabsTrigger>
                <TabsTrigger
                  value="music"
                  className="data-[state=active]:bg-[#00CC99] data-[state=active]:text-white font-bold"
                  data-testid="tab-music"
                >
                  <Music className="w-4 h-4 mr-2" />
                  Music
                </TabsTrigger>
              </TabsList>

              <TabsContent value="html" className="mt-4">
                <div className="brutal-card p-0 overflow-hidden">
                  <div className="bg-[#1A1A1A] p-2 border-b-2 border-black">
                    <span className="text-white text-sm font-mono">custom.html</span>
                  </div>
                  <Editor
                    value={customHTML}
                    onValueChange={setCustomHTML}
                    highlight={(code) => highlight(code, languages.markup, "markup")}
                    padding={16}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 14,
                      backgroundColor: "#1A1A1A",
                      color: "#fff",
                      minHeight: 300,
                    }}
                    data-testid="html-editor"
                  />
                </div>
              </TabsContent>

              <TabsContent value="css" className="mt-4">
                <div className="brutal-card p-0 overflow-hidden">
                  <div className="bg-[#1A1A1A] p-2 border-b-2 border-black">
                    <span className="text-white text-sm font-mono">custom.css</span>
                  </div>
                  <Editor
                    value={customCSS}
                    onValueChange={setCustomCSS}
                    highlight={(code) => highlight(code, languages.css, "css")}
                    padding={16}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 14,
                      backgroundColor: "#1A1A1A",
                      color: "#fff",
                      minHeight: 300,
                    }}
                    data-testid="css-editor"
                  />
                </div>
              </TabsContent>

              <TabsContent value="music" className="mt-4">
                <div className="brutal-card space-y-4">
                  <p className="text-[#4A4A4A] text-sm">
                    Add a song to your profile! It will appear as a music player.
                  </p>
                  
                  <div>
                    <Label htmlFor="song-title" className="font-bold">Song Title</Label>
                    <Input
                      id="song-title"
                      value={profileSong.title}
                      onChange={(e) => setProfileSong({ ...profileSong, title: e.target.value })}
                      className="brutal-input mt-1"
                      placeholder="e.g., Blinding Lights"
                      data-testid="song-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="song-artist" className="font-bold">Artist</Label>
                    <Input
                      id="song-artist"
                      value={profileSong.artist}
                      onChange={(e) => setProfileSong({ ...profileSong, artist: e.target.value })}
                      className="brutal-input mt-1"
                      placeholder="e.g., The Weeknd"
                      data-testid="song-artist-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="song-url" className="font-bold">Song URL (optional)</Label>
                    <Input
                      id="song-url"
                      type="url"
                      value={profileSong.url}
                      onChange={(e) => setProfileSong({ ...profileSong, url: e.target.value })}
                      className="brutal-input mt-1"
                      placeholder="Spotify or YouTube link"
                      data-testid="song-url-input"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Tips */}
            <div className="brutal-card bg-[#E3F4EA]">
              <h3 className="font-bold mb-2">Tips</h3>
              <ul className="text-sm text-[#4A4A4A] space-y-1">
                <li>• Use <code className="bg-white px-1">.custom-profile-container</code> to style your HTML container</li>
                <li>• MySpace vibes: try <code className="bg-white px-1">&lt;marquee&gt;</code> or sparkle GIFs!</li>
                <li>• Keep it fun but readable for visitors</li>
                <li>• Changes are live - check the preview!</li>
              </ul>
            </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 font-bold text-lg">
              <Eye className="w-5 h-5" />
              Live Preview
            </div>
            
            <div className="brutal-card bg-white min-h-[400px]">
              {/* Custom CSS Preview */}
              <style dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(customCSS, { FORCE_BODY: true }) }} />
              
              {/* Custom HTML Preview */}
              <div
                className="custom-profile-container"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(customHTML) }}
                data-testid="preview-container"
              />

              {/* Music Player Preview */}
              {profileSong.title && (
                <div className="mt-4 p-4 bg-[#1A1A1A] border-2 border-black text-white">
                  <div className="flex items-center gap-3">
                    <Music className="w-6 h-6" />
                    <div>
                      <p className="font-bold">{profileSong.title}</p>
                      <p className="text-sm opacity-70">{profileSong.artist}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Preview Profile Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/profile/${user?.user_id}`)}
              className="brutal-btn w-full"
              data-testid="preview-profile-btn"
            >
              View Full Profile
            </motion.button>
          </div>
        </div>
      </main>
    </div>
  );
}
